import { QueryObserver } from "@tanstack/react-query"
import { computed, observable, runInAction } from "mobx"

import { projectColorPalette } from "@/components/ProjectCoverEditor/ProjectColorPalette.ts"
import { batchInfo } from "@/lib/apiClient/client.ts"
import { withGlobalLoader } from "@/lib/context.ts"
import { pluralize } from "@/lib/displayUtils.ts"
import { Model } from "@/lib/Model.ts"
import { favoriteProjectsQueryKey, projectsV2QueryKey } from "@/lib/queries.ts"

import type { Batch, PR_Project2, ProjectKey } from "@/lib/client/generated/TypeDefinitions.ts"
import type { OrgContext } from "@/lib/context.ts"
import type { IComputedValue, IObservableValue } from "mobx"
import type { MouseEvent } from "react"


export class ProjectSelectorModel extends Model {
  private elementNode: HTMLElement | null = null

  private projectColors: Record<string, string> = {}

  private readonly elements = observable.box<PR_Project2[]>([], { deep: false })

  static getBatchSize() {
    return 2 * Math.max(5, Math.ceil(window.innerWidth / 320))
  }

  private nextOffset: string | null = null
  readonly loadingNext = observable.box(false)
  private hasMore = true

  readonly projects: IComputedValue<ProjectItemModel[]>
  private readonly favoriteProjects = observable.box<ProjectKey[]>([], { deep: false })

  readonly selected = observable.box<ProjectItemModel | null>(null)
  readonly settingsDialogOpen = observable.box(false)

  constructor(readonly ctx: OrgContext, projects: Batch<PR_Project2>, favoriteProjects: ProjectKey[]) {
    super("ProjectSelectorModel")

    this.nextOffset = projects.next
    this.hasMore = projects.data.length === ProjectSelectorModel.getBatchSize()

    this.projectColors = this.assignColors(projects.data, this.projectColors)

    this.elements = observable.box(projects.data, { deep: false })
    this.favoriteProjects = observable.box(favoriteProjects, { deep: false })

    // Refetch on project list invalidation
    const observer = new QueryObserver(ctx.queryClient, {
      queryKey: projectsV2QueryKey,
      queryFn: () => ctx.queries.listProjectsV2(batchInfo(this.elements.get().length))
    })

    const unsubscribe = observer.subscribe((event) => {
      if (event.isStale && !event.isFetching && !event.isRefetching) {
        void ctx.queries.listProjectsV2(batchInfo(this.elements.get().length)).then((projects) => {
          runInAction(() => this.elements.set(projects.data))
        })
      }
    })

    this.lifetime.add(() => {
      unsubscribe()
    }, "ProjectSelectorModel:unsubscribe")

    this.projects = computed(() => {
      return this.elements.get()
        .map(project => {
          let projectColor = this.projectColors[project.key.key]
          if (project.colorKey) {
            projectColor = projectColorPalette[project.colorKey] || projectColor
          }

          return new ProjectItemModel(ctx, project, this.favoriteProjects, projectColor)
        })
        .sort((a, b) => {
          // Favorite projects should be at the top of the list
          if (a.favorite !== b.favorite) {
            return Number(b.favorite) - Number(a.favorite)
          }

          return a.title.localeCompare(b.title)
        })
    })
  }

  private async loadNext() {
    if (!this.hasMore || !this.nextOffset || this.loadingNext.get()) {
      return
    }

    runInAction(() => this.loadingNext.set(true))

    try {
      const batch = await withGlobalLoader(this.ctx, this.ctx.queries.listProjectsV2(batchInfo(ProjectSelectorModel.getBatchSize(), this.nextOffset)))
      runInAction(() => {
        this.projectColors = this.assignColors(batch.data, this.projectColors)
        this.elements.set([...this.elements.get(), ...batch.data])
        this.nextOffset = batch.next
        this.hasMore = batch.data.length === ProjectSelectorModel.getBatchSize()
      })
    } finally {
      runInAction(() => this.loadingNext.set(false))
    }
  }

  readonly refCallback = (element: HTMLElement | null) => {
    if (!element || this.elementNode === element) {
      return
    }

    this.elementNode = element

    const onScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } = element
      if (scrollLeft + clientWidth >= scrollWidth - 320) {
        void this.loadNext()
      }
    }

    const onWheel = (e: WheelEvent) => {
      // Vertical scroll -> horizontal movement
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        e.preventDefault()
        element.scrollLeft += e.deltaY
      }
    }

    element.addEventListener("scroll", onScroll)
    element.addEventListener("wheel", onWheel, { passive: false })

    this.lifetime.add(() => {
      element.removeEventListener("scroll", onScroll)
      element.removeEventListener("wheel", onWheel)
    }, "ProjectSelectorModel:refCallback")
  }

  readonly showSettings = (project: ProjectItemModel) => {
    runInAction(() => {
      this.selected.set(project)
      this.settingsDialogOpen.set(true)
    })
  }

  readonly setSettingsDialogOpen = (open: boolean) => {
    runInAction(() => {
      this.settingsDialogOpen.set(open)
      if (!open) {
        this.selected.set(null)
      }
    })
  }

  private assignColors(projects: PR_Project2[], projectColors: Record<string, string> = {}): Record<string, string> {
    projects.forEach(project => {
      if (project.colorKey) {
        this.projectColors[project.key.key] = projectColorPalette[project.colorKey]
      }
    })

    const paletteColors = Object.values(projectColorPalette)
    projectColors = Object.assign({}, projectColors)

    const result: Record<string, string> = {}

    let paletteIndex = 0
    for (let i = 0; i < projects.length; i++) {
      const project = projects[i]
      const projectKey = project.key.key

      const current = projectColors[projectKey] || null
      if (current !== null) {
        result[projectKey] = current
        continue
      }

      let attempts = 0
      while (attempts < paletteColors.length) {
        const color = paletteColors[paletteIndex]

        const prev = result[projects[i - 1]?.key.key] ?? null
        const next = projectColors[projects[i + 1]?.key.key] ?? null

        if (color !== prev && color !== next) {
          result[projectKey] = color
          break
        }

        paletteIndex = (paletteIndex + 1) % paletteColors.length
        attempts += 1
      }

      if (result[projectKey] === undefined) {
        result[projectKey] = paletteColors[paletteIndex]
        paletteIndex = (paletteIndex + 1) % paletteColors.length
      }

      paletteIndex = (paletteIndex + 1) % paletteColors.length
    }

    return result
  }
}

export class ProjectItemModel {
  readonly favorite: IObservableValue<boolean>
  readonly isDropdownOpen = observable.box(false)

  constructor(
    private readonly ctx: OrgContext,
    private readonly project: PR_Project2,
    private readonly favoriteProjects: IObservableValue<ProjectKey[]>,
    private readonly cardColor: string
  ) {
    this.favorite = observable.box(this.isFavorite())
  }

  get coverUrl() {
    if (!this.project.cover) {
      return null
    }

    return this.ctx.queries.apiClient.buildStaticUrl(`/attachments/${this.project.cover}`)
  }

  get id() {
    return this.project.id
  }

  get key() {
    return this.project.key
  }

  get data() {
    return this.project
  }

  get title() {
    return this.project.name
  }

  get subtitle() {
    const project = this.project

    if (project.initProjectTaskId) {
      return "Complete the setup"
    }

    if (!project.totalTasks) {
      return "Ready to get started"
    }

    return `${project.totalTasks} ${pluralize("idea", project.totalTasks)}`
  }

  get color() {
    return this.coverUrl ? undefined : this.cardColor
  }

  get isEmpty() {
    return this.project.totalTasks === 0
  }

  get initProjectTaskId() {
    return this.project.initProjectTaskId
  }

  readonly onDropdownClick = (e: MouseEvent) => {
    e.preventDefault()

    this.setDropdownOpen(!this.isDropdownOpen.get())
  }

  readonly setDropdownOpen = (open: boolean) => {
    runInAction(() => this.isDropdownOpen.set(open))
  }

  readonly toggleFavorite = async (favorite: boolean) => {
    runInAction(() => {
      const favorites = this.favoriteProjects.get().filter(it => it.key !== this.project.key.key)
      favorite && favorites.push(this.project.key)

      this.favorite.set(favorite)
      this.favoriteProjects.set(favorites)
    })

    await withGlobalLoader(this.ctx, this.toggleFavoriteProject(this.project.key, favorite))
  }

  private readonly toggleFavoriteProject = async (projectKey: ProjectKey, favorite: boolean) => {
    await this.ctx.queries.apiClient.toggleFavoriteProject(projectKey, favorite)
    void this.ctx.queryClient.invalidateQueries({ queryKey: favoriteProjectsQueryKey })
  }

  private isFavorite() {
    return this.favoriteProjects.get().some(it => it.key === this.project.key.key)
  }
}